// ==========================================
// PeopleCore Types
// ==========================================

export type ContractType = 'CLT' | 'PJ';
export type EmployeeStatus = 'ACTIVE' | 'INACTIVE' | 'VACATION';
export type InsightSentiment = 'POSITIVE' | 'NEUTRAL' | 'NEGATIVE';

// Labels para sentimentos de insights
export const insightSentimentLabels: Record<InsightSentiment, string> = {
  POSITIVE: 'Positivo',
  NEUTRAL: 'Neutro',
  NEGATIVE: 'Negativo',
};

export interface Employee {
  id: string;
  userId: string;
  dailyWorkHours: number;
  timezone: string;
  // Dados Pessoais
  rg?: string | null;
  cpf?: string | null;
  birthDate?: string | null;
  phone?: string | null;
  pixKey?: string | null;
  bankAccount?: string | null;
  notionName?: string | null; // ID da relation do Emissor no Notion
  // Endereço
  zipCode?: string | null;
  street?: string | null;
  number?: string | null;
  complement?: string | null;
  neighborhood?: string | null;
  city?: string | null;
  state?: string | null;
  // Dados Contratuais
  admissionDate?: string | null;
  contractType?: ContractType | null;
  status?: EmployeeStatus;
  jobPositionId?: string | null;
  managerId?: string | null;
  // Benefícios
  benefitVAVR?: number | null;
  benefitHomeOffice?: number | null;
  benefitVT?: number | null;
  benefitVTDiscount?: number;
  benefitVTPaymentMethod?: 'PLUXEE' | 'PIX'; // VT no cartão Pluxee ou no Pix (soma no pagamento)
  hasGympass?: boolean;
  // Dados de Sócio
  isPartner?: boolean;
  partnershipPercentage?: number | null;
  monthlyProfitAdvance?: number | null;
  createdAt: string;
  updatedAt: string;
  user?: {
    id: string;
    name: string;
    email: string;
    role: string;
    whatsapp?: string | null;
    slackUserId?: string | null;
  };
  jobPosition?: JobPosition;
  manager?: Employee;
  subordinates?: Employee[];
  salaryHistory?: SalaryHistory[];
  currentSalary?: SalaryHistory | null; // Salário atual do colaborador
  performanceInsights?: PerformanceInsight[];
}

export interface Department {
  id: string;
  name: string;
  description?: string | null;
  isActive: boolean;
  createdAt?: string;
  updatedAt?: string;
  jobPositions?: JobPosition[];
}

export interface JobPosition {
  id: string;
  name: string;
  departmentId: string;
  baseSalary?: number | null;
  isActive: boolean;
  createdAt?: string;
  updatedAt?: string;
  department?: Department;
  employees?: Employee[];
}

export interface SalaryHistory {
  id: string;
  employeeId: string;
  value: number; // Mantido para compatibilidade
  employeeSalary?: number | null; // Salário bruto do colaborador
  reason?: string | null;
  createdAt: string;
}

export interface PerformanceInsight {
  id: string;
  employeeId: string;
  date: string;
  content: string;
  sentiment: InsightSentiment;
  metadata?: {
    period?: {
      start: string;
      end: string;
    };
    messageCount?: number;
    score?: number;
    highlights?: string[];
    improvements?: string[];
  };
  createdAt: string;
}

export interface HRDashboardData {
  employeesByDepartment: Array<{
    departmentId: string;
    departmentName: string;
    count: number;
  }>;
  totalPayrollCost: {
    total: number;
    employeeCount: number;
  };
  birthdays: Array<{
    id: string;
    name: string;
    birthDate: string;
    daysUntilBirthday: number;
  }>;
  upcomingBirthdays: Array<{
    id: string;
    name: string;
    birthDate: string;
    daysUntilBirthday: number;
  }>;
  workAnniversaries: Array<{
    id: string;
    name: string;
    admissionDate: string;
    yearsOfService: number;
  }>;
  totalEmployees: number;
  activeEmployees: number;
  inactiveEmployees: number;
}

export interface TimeEntry {
  id: string;
  employeeId: string;
  date: string;
  clockIn: string | null;
  clockOut: string | null;
  breakStart: string | null;
  breakEnd: string | null;
  timezone: string;
  createdAt: string;
  updatedAt: string;
}

export interface EmployeeProjectAllocation {
  id: string;
  employeeId: string;
  projectId: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  employee?: Employee;
}

export interface Project {
  id: string;
  name: string;
  description: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  activities?: Activity[];
  employeeAllocations?: EmployeeProjectAllocation[];
}

export interface Activity {
  id: string;
  projectId: string;
  name: string;
  description: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface TimeAllocation {
  id: string;
  employeeId: string;
  activityId: string;
  date: string;
  hours: number;
  description: string | null;
  createdAt: string;
  updatedAt: string;
  activity?: Activity & { project?: Project };
}

export interface Timesheet {
  id: string;
  employeeId: string;
  date: string;
  hoursWorked: number;
  expectedHours: number;
  hourBalance: number;
  accumulatedBalance: number;
  approvedBalance: number;
  pendingAdjustmentsHours: number;
  entries: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

export type AdjustmentStatus = 'PENDING' | 'APPROVED' | 'REJECTED';

export interface TimeAdjustmentRequest {
  id: string;
  employeeId: string;
  date: string;
  originalData: {
    clockIn?: string | null;
    clockOut?: string | null;
    breakStart?: string | null;
    breakEnd?: string | null;
  } | null;
  requestedData: {
    clockIn?: string;
    clockOut?: string;
    breakStart?: string;
    breakEnd?: string;
  };
  justification: string;
  status: AdjustmentStatus;
  reviewedBy: string | null;
  reviewedAt: string | null;
  reviewNote: string | null;
  createdAt: string;
  updatedAt: string;
  employee?: Employee;
  sla?: {
    deadline: string;
    hoursRemaining: number;
    hoursElapsed: number;
    isOverdue: boolean;
    status: 'ok' | 'warning' | 'overdue' | 'completed';
  };
}

export type ClockStatus = 'idle' | 'clocked-in' | 'on-break' | 'clocked-out';

export function getClockStatus(entry: TimeEntry | null): ClockStatus {
  if (!entry) return 'idle';
  if (entry.clockOut) return 'clocked-out';
  if (entry.breakStart && !entry.breakEnd) return 'on-break';
  // Se já voltou do almoço (tem breakEnd), está trabalhando novamente
  if (entry.clockIn && entry.breakEnd) return 'clocked-in';
  // Se tem entrada mas ainda não saiu para almoço, está trabalhando
  if (entry.clockIn && !entry.breakStart) return 'clocked-in';
  return 'idle';
}

export function formatHours(hours: number): string {
  const h = Math.floor(Math.abs(hours));
  const m = Math.round((Math.abs(hours) - h) * 60);
  const sign = hours < 0 ? '-' : '';
  return `${sign}${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

export function formatTimeBRT(isoString: string | null | undefined): string {
  if (!isoString) return '--:--';
  return new Date(isoString).toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'America/Sao_Paulo',
  });
}

export function formatDateBRT(isoString: string | null | undefined): string {
  if (!isoString) return '';
  // Para campos @db.Date, usar UTC para evitar problema de timezone
  // Um campo de data pura "2026-03-03" deve mostrar "03 de março" sempre
  return new Date(isoString).toLocaleDateString('pt-BR', {
    weekday: 'long',
    day: '2-digit',
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

/**
 * Formata o SLA de forma amigável
 * Ex: "Faltam 12h", "Faltam 2d 4h", "Atrasado há 5h"
 */
export function formatSLA(hoursRemaining: number): string {
  const absHours = Math.abs(hoursRemaining);
  const isOverdue = hoursRemaining < 0;

  if (absHours < 1) {
    const minutes = Math.round(absHours * 60);
    return isOverdue ? `Atrasado há ${minutes}min` : `Faltam ${minutes}min`;
  }

  if (absHours < 24) {
    const hours = Math.floor(absHours);
    const minutes = Math.round((absHours - hours) * 60);
    const hoursStr = minutes > 0 ? `${hours}h${minutes}min` : `${hours}h`;
    return isOverdue ? `Atrasado há ${hoursStr}` : `Faltam ${hoursStr}`;
  }

  const days = Math.floor(absHours / 24);
  const hours = Math.floor(absHours % 24);
  const timeStr = hours > 0 ? `${days}d ${hours}h` : `${days}d`;
  return isOverdue ? `Atrasado há ${timeStr}` : `Faltam ${timeStr}`;
}

export function formatDateShort(isoString: string | null | undefined): string {
  if (!isoString) return '';
  // Para campos @db.Date, usar UTC para evitar problema de timezone
  // Um campo de data pura "2026-03-03" deve mostrar "03/03/2026" sempre
  return new Date(isoString).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

/**
 * Converte uma data ISO string de um campo @db.Date para "YYYY-MM-DD"
 * IMPORTANTE: Sempre usar UTC para campos de data pura, nunca timezone local
 * Ex: "2026-03-03T00:00:00.000Z" -> "2026-03-03"
 */
export function toDateString(isoString: string): string {
  const date = new Date(isoString);
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Converte um objeto Date para "YYYY-MM-DD" no timezone BRT
 * Use para converter datas locais (como new Date()) para o formato de API
 */
export function formatDateForAPI(date: Date): string {
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Sao_Paulo',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  const parts = formatter.formatToParts(date);
  const year = parts.find(p => p.type === 'year')?.value;
  const month = parts.find(p => p.type === 'month')?.value;
  const day = parts.find(p => p.type === 'day')?.value;
  return `${year}-${month}-${day}`;
}
